import { useState, useRef, useEffect } from "react";
import { useFecharComEsc } from "../../hooks/useFecharComEsc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRascunho } from "../../hooks/useRascunho";
import { X, Plus, Trash2, Paperclip, UploadCloud, FileText, Image as ImageIcon, Music, Film, ClipboardList } from "lucide-react";
import { criarTarefa, atualizarTarefa, uploadAnexo, anexoUrlAbsoluto, Recorrencia, Tarefa } from "../../services/tarefas";
import { listarUsuariosAtivos } from "../../services/usuarios";
import { listarProjetos, rotuloProjeto } from "../../services/projetos";
import { listarTiposTarefa, mediaTipoTarefa } from "../../services/tiposTarefa";
import { listarFuncoesTarefa } from "../../services/funcoesTarefa";
import { MentionTextarea } from "../../components/MentionTextarea";
import { SearchableSelect } from "../../components/SearchableSelect";
import { useAuth } from "../../hooks/useAuth";
import { listarFormularios, salvarResposta } from "../../services/briefings";
import { CamposBriefingInline } from "../../components/BriefingSecao";

/**
 * Converte string de esforço pra horas decimais. Aceita:
 *   "1h30" / "1H30" → 1.5
 *   "1:30"          → 1.5
 *   "1.5" / "1,5"   → 1.5
 *   "30m"           → 0.5
 *   "" / inválido   → null
 */
function parseEsforco(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  // 1h30 ou 1h
  const mHM = s.match(/^(\d+)\s*h\s*(\d{0,2})?$/);
  if (mHM) {
    const h = parseInt(mHM[1], 10);
    const m = mHM[2] ? parseInt(mHM[2], 10) : 0;
    if (m >= 60) return null;
    return h + m / 60;
  }
  // 90m
  const mMin = s.match(/^(\d+)\s*m$/);
  if (mMin) return parseInt(mMin[1], 10) / 60;
  // 1:30
  const mColon = s.match(/^(\d+):(\d{1,2})$/);
  if (mColon) {
    const h = parseInt(mColon[1], 10);
    const m = parseInt(mColon[2], 10);
    if (m >= 60) return null;
    return h + m / 60;
  }
  // decimal puro
  const dec = parseFloat(s.replace(",", "."));
  if (Number.isFinite(dec) && dec >= 0) return dec;
  return null;
}

/** Horas decimais (1.5) → "01h30" (formato exibido no campo Esforço). */
function formatarEsforco(h: number): string {
  const horas = Math.floor(h);
  const minutos = Math.round((h - horas) * 60);
  return `${String(horas).padStart(2, "0")}h${String(minutos).padStart(2, "0")}`;
}

interface NovaTarefaModalProps {
  onClose: () => void;
  defaultProjetoId?: string | null;
  editing?: Tarefa | null;
  // MELHORIA 20 — abre a criação já preenchida (agendar na Agenda da equipe):
  // dia (prazo), pessoa (atribuidoA) e opcionalmente o tipo (ex.: captação).
  prefill?: { prazo?: string; atribuidoA?: string; tipoSlug?: string };
}

// Valor "sentinela" pro item "Sem projeto (avulsa)" no seletor: projeto virou
// obrigatório (Rec, jul/2026), mas avulsa segue possível — então "" = não
// escolheu (inválido) e AVULSA = escolheu explicitamente sem projeto.
const AVULSA = "__avulsa__";
// Slug do tipo "Captação": único tipo em que o link do Drive NÃO é obrigatório.
const SLUG_CAPTACAO = "captacao";

export function NovaTarefaModal({ onClose, defaultProjetoId, editing, prefill }: NovaTarefaModalProps) {
  const qc = useQueryClient();
  const { user, can } = useAuth();
  const ehEdicao = !!editing;

  const [titulo, setTitulo] = useState(editing?.titulo || "");
  const [descricao, setDescricao] = useState(editing?.descricao || "");
  // Pré-seleciona o próprio usuário como responsável quando criando (mais fluido).
  // Em edição, usa quem já está atribuído.
  const [atribuidoA, setAtribuidoA] = useState(editing?.atribuidoA || prefill?.atribuidoA || user?.id || "");
  // Responsáveis adicionais (além do principal). Múltiplas pessoas envolvidas.
  const [responsaveis, setResponsaveis] = useState<string[]>(
    editing?.responsaveis?.map((r) => r.usuarioId) || [],
  );
  // FUNÇÃO de cada pessoa NESTA tarefa (Edição, Motion, Captação…), por id.
  // Mapa à parte de propósito: `atribuidoA`/`responsaveis` continuam iguais, então
  // ★/× e a regra do "eu por padrão" seguem funcionando sem mudança.
  const [funcoesPessoa, setFuncoesPessoa] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    if (editing?.atribuidoA && (editing as any)?.funcaoPrincipal) m[editing.atribuidoA] = (editing as any).funcaoPrincipal;
    for (const r of editing?.responsaveis || []) if (r.funcao) m[r.usuarioId] = r.funcao;
    return m;
  });
  const { data: funcoesCatalogo = [] } = useQuery({
    queryKey: ["funcoes-tarefa"],
    queryFn: () => listarFuncoesTarefa(),
    staleTime: 5 * 60 * 1000,
  });
  // Tipo da tarefa (1 por tarefa) — define o checklist automático.
  const [tipoTarefaId, setTipoTarefaId] = useState<string>(editing?.tipoTarefaId || "");
  // Link da pasta do Drive.
  const [linkDrive, setLinkDrive] = useState(editing?.linkDrive || "");
  const [aprovadorId, setAprovadorId] = useState(editing?.aprovadorId || "");
  // Editando: se a tarefa não tem projeto, mostra "avulsa" selecionado (é uma
  // escolha válida). Criando: usa o projeto pré-definido (veio de um quadro de
  // projeto) ou "" (não escolheu — obrigatório escolher).
  const [projetoId, setProjetoId] = useState<string>(
    editing ? (editing.projetoId || AVULSA) : (defaultProjetoId || ""),
  );
  const [prazo, setPrazo] = useState(editing?.prazo ? editing.prazo.split("T")[0] : (prefill?.prazo || ""));
  // MELHORIA 16 — data de entrega COMBINADA com o cliente (separada do prazo
  // interno). Só gestor/admin vê e seta; alimenta a pauta em /calendario.
  const [dataEntregaCliente, setDataEntregaCliente] = useState(
    (editing as any)?.dataEntregaCliente ? String((editing as any).dataEntregaCliente).split("T")[0] : "",
  );
  // Hora opcional do prazo. Hidrata da edição quando prazoComHora=true.
  const [prazoHora, setPrazoHora] = useState(() => {
    if (!editing?.prazoComHora || !editing?.prazo) return "";
    const d = new Date(editing.prazo);
    if (isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  // Prioridade removida da UI da tarefa (pedido Fase 02); mantida no payload como
  // "media" só pra preservar a coluna no banco. Sem setter (não há mais campo).
  const [prioridade] = useState(editing?.prioridade || "media");
  // TAG removida da UI (jul/2026 — a Rec não usa). Mantemos só os slugs já
  // gravados pra não apagá-los ao editar uma tarefa antiga que tenha tag.
  const [tagSlugs] = useState<string[]>(() => {
    const csv = editing?.etiquetas || "";
    return csv.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
  });

  const { data: tiposTarefa = [] } = useQuery({
    queryKey: ["tipos-tarefa", "ativos"],
    queryFn: () => listarTiposTarefa(),
  });
  const tipoSelecionado = tiposTarefa.find((t) => t.id === tipoTarefaId);
  // MELHORIA 20 — pré-seleciona o tipo pelo slug do prefill (ex.: "Agendar
  // captação" na Agenda) assim que os tipos carregam. Só na criação, uma vez.
  const tipoPrefilled = useRef(false);
  useEffect(() => {
    if (ehEdicao || tipoPrefilled.current || !prefill?.tipoSlug || tipoTarefaId || tiposTarefa.length === 0) return;
    const achado = tiposTarefa.find((t) => t.slug === prefill.tipoSlug);
    if (achado) { setTipoTarefaId(achado.id); tipoPrefilled.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiposTarefa]);
  // MELHORIA 8 — só entram no dropdown as entradas marcadas "É tipo de tarefa"
  // (as vendáveis-puras ficam de fora). Mantém a atual em edição, se houver.
  const tiposParaTarefa = tiposTarefa.filter((t) => t.usadoComoTipo || t.id === editing?.tipoTarefaId || t.id === tipoTarefaId);
  // CARDS 1 — média histórica de esforço do tipo (dica). Só busca na criação.
  const { data: mediaTipo } = useQuery({
    queryKey: ["media-tipo-tarefa", tipoTarefaId],
    queryFn: () => mediaTipoTarefa(tipoTarefaId),
    enabled: !ehEdicao && !!tipoTarefaId,
  });

  const [recorrencia, setRecorrencia] = useState<Recorrencia | "">(editing?.recorrencia || "");
  // "Esforço" (horas estimadas) — print do PDF mostra "01h00".
  // Aceita "1.5", "1,5", "1:30", "1h30", "30m". Valor vazio = sem estimativa.
  const [esforco, setEsforco] = useState<string>(() => {
    const h = (editing as any)?.horasEstimadas as number | null | undefined;
    if (!h && h !== 0) return "";
    const horas = Math.floor(h);
    const minutos = Math.round((h - horas) * 60);
    return `${String(horas).padStart(2, "0")}h${String(minutos).padStart(2, "0")}`;
  });
  const [subitens, setSubitens] = useState<string[]>([]);
  const [novoSub, setNovoSub] = useState("");
  const [visivelParaCliente, setVisivelParaCliente] = useState(!!editing?.visivelParaCliente);
  // MELHORIA 15 — quantidade de peças entregues no card (>=1; default 1).
  const [quantidadePecas, setQuantidadePecas] = useState<string>(() => String((editing as any)?.quantidadePecas ?? 1));

  // Rascunho automático do formulário (Fase 02 #9/#10) — só na criação.
  const rascunho = useRascunho<any>("nova_tarefa", !ehEdicao);
  useEffect(() => {
    if (ehEdicao) return;
    if (!titulo.trim() && !descricao.trim()) return; // não persiste form vazio
    rascunho.salvar({ titulo, descricao, atribuidoA, responsaveis, tipoTarefaId, linkDrive, aprovadorId, projetoId, prazo, prazoHora, dataEntregaCliente, tagSlugs, recorrencia, esforco, quantidadePecas, subitens, visivelParaCliente });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titulo, descricao, atribuidoA, responsaveis, tipoTarefaId, linkDrive, aprovadorId, projetoId, prazo, prazoHora, dataEntregaCliente, tagSlugs, recorrencia, esforco, quantidadePecas, subitens, visivelParaCliente]);
  function restaurarRascunho() {
    const r = rascunho.rascunhoInicial;
    if (!r) return;
    if (r.titulo != null) setTitulo(r.titulo);
    if (r.descricao != null) setDescricao(r.descricao);
    if (r.atribuidoA != null) setAtribuidoA(r.atribuidoA);
    if (Array.isArray(r.responsaveis)) setResponsaveis(r.responsaveis);
    if (r.tipoTarefaId != null) setTipoTarefaId(r.tipoTarefaId);
    if (r.linkDrive != null) setLinkDrive(r.linkDrive);
    if (r.aprovadorId != null) setAprovadorId(r.aprovadorId);
    if (r.projetoId != null) setProjetoId(r.projetoId);
    if (r.prazo != null) setPrazo(r.prazo);
    if (r.prazoHora != null) setPrazoHora(r.prazoHora);
    if (r.dataEntregaCliente != null) setDataEntregaCliente(r.dataEntregaCliente);
    if (r.recorrencia != null) setRecorrencia(r.recorrencia);
    if (r.esforco != null) setEsforco(r.esforco);
    if (r.quantidadePecas != null) setQuantidadePecas(r.quantidadePecas);
    if (Array.isArray(r.subitens)) setSubitens(r.subitens);
    if (r.visivelParaCliente != null) setVisivelParaCliente(r.visivelParaCliente);
    rascunho.descartar();
  }
  // Anexos só na CRIAÇÃO. Na edição, usa o drawer.
  const [anexos, setAnexos] = useState<File[]>([]);
  const [dragHover, setDragHover] = useState(false);
  const [progressoUpload, setProgressoUpload] = useState<{ enviados: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Imagens coladas/arrastadas na DESCRIÇÃO durante a CRIAÇÃO. Aqui a tarefa
   * ainda não tem id, então não há onde anexar: guardamos o arquivo e devolvemos
   * uma URL temporária (blob:) só pra pessoa VER a imagem na hora. Depois de
   * criar a tarefa, subimos cada uma e trocamos a URL temporária pela definitiva
   * na descrição (ver resolverImagensPendentes). Antes disso o colar simplesmente
   * não funcionava na criação — reclamação do Ricardo (jul/2026).
   */
  const imagensPendentesRef = useRef<Map<string, File>>(new Map());

  // Libera as URLs temporárias ao fechar (evita vazamento de memória).
  useEffect(() => () => {
    for (const url of imagensPendentesRef.current.keys()) {
      try { URL.revokeObjectURL(url); } catch { /* ignora */ }
    }
    imagensPendentesRef.current.clear();
  }, []);

  /** Guarda o arquivo e devolve uma URL temporária pra exibir inline já. */
  function guardarImagemPendente(file: File): string {
    const url = URL.createObjectURL(file);
    imagensPendentesRef.current.set(url, file);
    return url;
  }

  /**
   * Sobe as imagens pendentes pra tarefa recém-criada e troca as URLs blob: pelas
   * definitivas no texto. Se alguma falhar, remove o `![](blob:…)` do texto pra
   * não deixar imagem quebrada. Devolve a descrição corrigida (ou null se não
   * havia nada pendente).
   */
  async function resolverImagensPendentes(tarefaId: string, texto: string): Promise<string | null> {
    const pendentes = [...imagensPendentesRef.current.entries()].filter(([url]) => texto.includes(url));
    if (pendentes.length === 0) return null;
    let novo = texto;
    for (const [tempUrl, file] of pendentes) {
      try {
        const anexo = await uploadAnexo(tarefaId, file);
        novo = novo.split(tempUrl).join(anexoUrlAbsoluto(anexo.url));
      } catch {
        // Falhou o upload: tira a imagem quebrada do texto.
        novo = novo.replace(new RegExp(`!\\[[^\\]]*\\]\\(${tempUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)`, "g"), "");
      }
      try { URL.revokeObjectURL(tempUrl); } catch { /* ignora */ }
      imagensPendentesRef.current.delete(tempUrl);
    }
    return novo;
  }

  function adicionarArquivos(files: FileList | File[] | null) {
    if (!files) return;
    const lista = Array.from(files);
    if (lista.length === 0) return;
    setAnexos((prev) => [...prev, ...lista]);
  }
  function removerAnexo(idx: number) {
    setAnexos((prev) => prev.filter((_, i) => i !== idx));
  }

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  /**
   * ALOCADOS — a UI mostra UMA lista só, mas o backend continua esperando
   * `atribuidoA` (a principal) + `responsaveis[]` (as demais). Estes helpers
   * fazem a ponte: a 1ª posição da lista é sempre a principal.
   */
  const alocados = [
    ...(atribuidoA ? [atribuidoA] : []),
    ...responsaveis.filter((id) => id !== atribuidoA),
  ];
  /**
   * Na CRIAÇÃO, `atribuidoA` já nasce com o usuário logado ("eu, por padrão").
   * Antes, com o campo único, adicionar alguém deixaria essa pessoa como
   * SECUNDÁRIA e quem criou continuaria principal — e como as peças entregues
   * contam pro principal, toda tarefa que a Thainá abrisse pontuaria pra ela em
   * vez do editor. Então: enquanto ela não mexeu na lista, a 1ª pessoa
   * adicionada SUBSTITUI o "eu" padrão (é o mesmo efeito do antigo "Quem está
   * fazendo", que era um seletor de troca). Depois disso, soma normalmente.
   */
  const listaTocada = useRef(false);
  /**
   * Puxa a FUNÇÃO PADRÃO do cadastro da pessoa ao alocá-la (pedido da Thainá,
   * ago/2026: "tem gente que sempre faz a mesma coisa"). Só preenche se aquela
   * pessoa ainda não tem função NESTE card — nunca sobrescreve escolha manual,
   * nem quando alguém é removido e re-adicionado.
   */
  function sugerirFuncao(id: string) {
    const padrao = usuarios.find((u) => u.id === id)?.funcaoPadrao;
    if (!padrao) return;
    setFuncoesPessoa((m) => (m[id] ? m : { ...m, [id]: padrao }));
  }
  // Na CRIAÇÃO a tarefa já nasce com alguém alocado (eu, ou o prefill da agenda)
  // sem passar por `adicionarAlocado` — então a função dessa 1ª pessoa é sugerida
  // aqui, assim que a lista de usuários chega. Só uma vez, e nunca em edição
  // (num card já salvo, "sem função" pode ser escolha deliberada).
  const sugeriuInicial = useRef(false);
  useEffect(() => {
    if (ehEdicao || sugeriuInicial.current || !atribuidoA || usuarios.length === 0) return;
    sugeriuInicial.current = true;
    sugerirFuncao(atribuidoA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarios.length, atribuidoA, ehEdicao]);

  function adicionarAlocado(id: string) {
    const soEuPadrao = !ehEdicao && !listaTocada.current && atribuidoA === user?.id && responsaveis.length === 0;
    listaTocada.current = true;
    sugerirFuncao(id);
    if (!atribuidoA || soEuPadrao) { setAtribuidoA(id); return; }  // vira a principal
    setResponsaveis((arr) => (arr.includes(id) ? arr : [...arr, id]));
  }
  function removerAlocado(id: string) {
    listaTocada.current = true;
    if (id !== atribuidoA) { setResponsaveis((arr) => arr.filter((x) => x !== id)); return; }
    // Tirou a principal: a próxima da fila assume (se houver).
    const proxima = responsaveis.find((x) => x !== atribuidoA) || "";
    setAtribuidoA(proxima);
    setResponsaveis((arr) => arr.filter((x) => x !== proxima));
  }
  function tornarPrincipal(id: string) {
    listaTocada.current = true;
    const anterior = atribuidoA;
    setAtribuidoA(id);
    setResponsaveis((arr) => {
      const sem = arr.filter((x) => x !== id);
      return anterior && anterior !== id && !sem.includes(anterior) ? [...sem, anterior] : sem;
    });
  }
  // Aprovador começa visível só se a tarefa já tiver um (senão fica no "+").
  const [mostrarAprovador, setMostrarAprovador] = useState(!!editing?.aprovadorId);

  /**
   * BRIEFING NA CRIAÇÃO (pedido da Thainá, ago/2026): antes ela preenchia a
   * "Descrição" (o briefing aberto) ao criar e DEPOIS abria o card pra responder
   * o formulário — o mesmo trabalho duas vezes. Agora, se o TIPO escolhido tiver
   * formulário vinculado, ele aparece aqui NO LUGAR da descrição. As respostas
   * são salvas logo após a tarefa ser criada (precisa do id).
   * NÃO trava: dá pra criar sem responder tudo e completar depois no card.
   */
  const { data: formsDoTipo = [] } = useQuery({
    queryKey: ["briefings", "para-tipo", tipoTarefaId || "-"],
    queryFn: () => listarFormularios({ tipoTarefaId: tipoTarefaId || undefined }),
    enabled: !!tipoTarefaId && !ehEdicao,
  });
  const formBriefing = ehEdicao ? undefined : formsDoTipo[0];
  const [respostasBriefing, setRespostasBriefing] = useState<Record<string, any>>({});
  // Com formulário, a observação livre fica escondida atrás de um "+".
  const [mostrarObs, setMostrarObs] = useState(false);
  // Trocou de tipo → o formulário é outro; zera o que foi respondido do anterior.
  useEffect(() => { setRespostasBriefing({}); }, [tipoTarefaId]);
  const { data: projetosData } = useQuery({
    queryKey: ["projetos-list", "internos+externos"],
    queryFn: () => listarProjetos({ perPage: 200, interno: "all" }),
  });
  const projetos: Array<{ id: string; nome: string }> = (projetosData as any)?.projetos || [];

  // Monta o ISO local (sem TZ) que o backend interpreta no fuso do servidor.
  // Quando hora não é informada, default 12:00 evita "voltar um dia" no fuso UTC.
  const prazoFinal = prazoHora ? `${prazo}T${prazoHora}:00` : `${prazo}T12:00:00`;
  const prazoComHora = !!prazoHora;
  // MELHORIA 16 — data do cliente (meio-dia pra não deslocar por fuso). "" = sem data.
  const entregaClienteFinal = dataEntregaCliente ? `${dataEntregaCliente}T12:00:00` : null;

  // Converte "1h30", "1:30", "1.5", "1,5", "90m" pra horas decimais (1.5).
  // Retorna null pra string vazia ou inválida.
  const horasEstimadas = parseEsforco(esforco);
  // MELHORIA 15 — quantidade de peças (inteiro >=1).
  const pecasNum = Math.max(1, Math.trunc(Number(String(quantidadePecas).replace(",", ".")) || 1));

  // Projeto pro payload: sentinela "avulsa" vira null; "" (não escolhido) vira null.
  const projetoFinal = projetoId === AVULSA ? null : (projetoId || null);
  // Obrigatórios novos (Rec, jul/2026, melhoria 5):
  const ehCaptacao = tipoSelecionado?.slug === SLUG_CAPTACAO;
  const projetoOk = projetoId !== ""; // escolheu um projeto OU "avulsa"
  const tipoOk = !!tipoTarefaId;
  const esforcoOk = horasEstimadas != null && horasEstimadas > 0;
  const linkOk = ehCaptacao || linkDrive.trim().length > 0; // link só é opcional em captação

  const criarM = useMutation({
    mutationFn: async () => {
      // Sem responsável escolhido → atribui a mim mesmo (fallback gentil).
      const responsavelFinal = atribuidoA || user?.id || "";
      const tarefa = await criarTarefa({
        titulo,
        descricao: descricao || undefined,
        atribuidoA: responsavelFinal,
        funcaoPrincipal: funcoesPessoa[responsavelFinal] || null,
        responsaveis: responsaveis
          .filter((r) => r !== responsavelFinal)
          .map((usuarioId) => ({ usuarioId, funcao: funcoesPessoa[usuarioId] || null })),
        tipoTarefaId: tipoTarefaId || null,
        linkDrive: linkDrive.trim() || null,
        aprovadorId: aprovadorId || null,
        projetoId: projetoFinal,
        prazo: prazoFinal,
        prazoComHora,
        prioridade,
        etiquetas: tagSlugs.length > 0 ? tagSlugs : undefined,
        recorrencia: recorrencia || null,
        subitens: subitens.length > 0 ? subitens : undefined,
        visivelParaCliente,
        horasEstimadas,
        quantidadePecas: pecasNum,
        dataEntregaCliente: entregaClienteFinal,
      } as any);

      // Sobe anexos sequencialmente (mostra progresso). Best-effort: se um falhar, segue.
      if (anexos.length > 0) {
        setProgressoUpload({ enviados: 0, total: anexos.length });
        for (let i = 0; i < anexos.length; i++) {
          try {
            await uploadAnexo(tarefa.id, anexos[i]);
          } catch {
            // ignora erro individual — usuário vê o que faltou via drawer
          }
          setProgressoUpload({ enviados: i + 1, total: anexos.length });
        }
      }

      // Briefing preenchido na criação: salva agora que a tarefa tem id.
      // Best-effort: se falhar, a tarefa já existe e o briefing pode ser
      // preenchido no card (não vale perder a tarefa por causa disso).
      if (formBriefing && Object.keys(respostasBriefing).length > 0) {
        try {
          await salvarResposta({
            formularioId: formBriefing.id,
            tarefaId: tarefa.id,
            valores: respostasBriefing,
          });
        } catch {
          /* segue — dá pra completar depois no card */
        }
      }

      // Imagens coladas/arrastadas na descrição: agora que existe id, sobe e
      // troca a URL temporária pela definitiva no texto.
      const descricaoCorrigida = await resolverImagensPendentes(tarefa.id, descricao || "");
      if (descricaoCorrigida != null && descricaoCorrigida !== descricao) {
        try {
          await atualizarTarefa(tarefa.id, { descricao: descricaoCorrigida } as any);
        } catch {
          // best-effort: a imagem já está nos anexos da tarefa
        }
      }
      return tarefa;
    },
    onSuccess: () => {
      rascunho.limpar(); // tarefa criada → descarta rascunho
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      setProgressoUpload(null);
      onClose();
    },
    onError: () => setProgressoUpload(null),
  });

  const updateM = useMutation({
    mutationFn: () =>
      atualizarTarefa(editing!.id, {
        titulo,
        descricao: descricao || null,
        atribuidoA,
        funcaoPrincipal: funcoesPessoa[atribuidoA] || null,
        responsaveis: responsaveis
          .filter((r) => r !== atribuidoA)
          .map((usuarioId) => ({ usuarioId, funcao: funcoesPessoa[usuarioId] || null })),
        tipoTarefaId: tipoTarefaId || null,
        linkDrive: linkDrive.trim() || null,
        aprovadorId: aprovadorId || null,
        projetoId: projetoFinal,
        prazo: prazoFinal,
        prazoComHora,
        prioridade,
        etiquetas: tagSlugs,
        recorrencia: recorrencia || null,
        visivelParaCliente,
        horasEstimadas,
        quantidadePecas: pecasNum,
        dataEntregaCliente: entregaClienteFinal,
      } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      qc.invalidateQueries({ queryKey: ["tarefa", editing!.id] });
      onClose();
    },
  });

  const submitting = criarM.isPending || updateM.isPending;
  // Responsável vira automático (eu mesmo) se ficar vazio na criação. Na edição, exige.
  const responsavelOk = ehEdicao ? !!atribuidoA : !!(atribuidoA || user?.id);
  // Obrigatórios (melhoria 5): título, responsável, prazo, projeto, tipo,
  // esforço e link (exceto captação). Lista o que falta pra ajudar quem preenche.
  const faltando: string[] = [];
  if (!titulo.trim()) faltando.push("título");
  if (!prazo) faltando.push("prazo");
  if (!projetoOk) faltando.push("projeto");
  if (!tipoOk) faltando.push("tipo de tarefa");
  if (!esforcoOk) faltando.push("esforço");
  if (!linkOk) faltando.push("link do Drive");
  const podeSalvar = responsavelOk && faltando.length === 0;

  // ESC fecha (pedido da Thainá) — confirmando se já tem coisa preenchida.
  useFecharComEsc(onClose, () =>
    titulo.trim() !== (editing?.titulo || "") ||
    descricao.trim() !== (editing?.descricao || "") ||
    subitens.length > 0 || anexos.length > 0 || novoSub.trim().length > 0
  );

  return (
    // Clicar no fundo NÃO fecha (pedido da Rec, jul/2026): gente perdia o
    // preenchimento inteiro com um clique fora sem querer. Só o X ou Salvar fecham.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{ehEdicao ? "Editar tarefa" : "Nova tarefa"}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm"
            title="Fechar sem salvar"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-[11px] text-slate-500 px-5 pt-3">
          Campos com <span className="text-red-500">*</span> são obrigatórios.
        </p>

        <div className="p-5 pt-3 space-y-3">
          {!ehEdicao && rascunho.temRascunho && (
            <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-800">Tem um rascunho não salvo desta tarefa.</span>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={restaurarRascunho} className="px-2.5 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">Restaurar</button>
                <button type="button" onClick={() => rascunho.descartar()} className="px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-600 text-xs hover:bg-slate-50">Descartar</button>
              </div>
            </div>
          )}
          {/* TIPO vem ANTES do título (pedido da Rec, ago/2026): é ele que define
              qual formulário de briefing aparece logo abaixo — escolher o tipo
              primeiro é a ordem natural de preenchimento. */}
          <div>
            <label className="text-xs text-slate-600">Tipo de tarefa <span className="text-red-500">*</span></label>
            <select
              value={tipoTarefaId}
              onChange={(e) => {
                const novoId = e.target.value;
                setTipoTarefaId(novoId);
                // CARDS 1 — auto-preenche o esforço com o padrão do tipo, sem
                // sobrescrever o que o usuário já digitou.
                const tipo = tiposTarefa.find((t) => t.id === novoId);
                if (tipo?.horasEstimadasPadrao && esforco.trim() === "") {
                  setEsforco(formatarEsforco(tipo.horasEstimadasPadrao));
                }
              }}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
            >
              <option value="" disabled>Selecione o tipo…</option>
              {editing?.tipoTarefaId && !tiposParaTarefa.some((t) => t.id === editing.tipoTarefaId) && (
                <option value={editing.tipoTarefaId}>(tipo inativo)</option>
              )}
              {tiposParaTarefa.map((t) => (
                <option key={t.id} value={t.id}>{t.icone} {t.label}</option>
              ))}
            </select>
            {!ehEdicao && tipoSelecionado && (tipoSelecionado.itensChecklist?.length ?? 0) > 0 && (
              <p className="text-[11px] text-emerald-600 mt-0.5">
                Checklist do processo será aplicado automaticamente ({tipoSelecionado.itensChecklist!.length} itens).
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-600">Título <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Revisar contrato do Cliente A"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 outline-none text-sm"
            />
          </div>

          {/* BRIEFING do tipo escolhido — ocupa o lugar da descrição pra não
              preencher a mesma coisa duas vezes (pedido da Thainá). */}
          {formBriefing && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 p-3">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList size={14} className="text-blue-600 shrink-0" />
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{formBriefing.nome}</span>
                <span className="text-[10px] text-slate-400">pode completar depois no card</span>
              </div>
              <CamposBriefingInline
                form={formBriefing}
                valores={respostasBriefing}
                onChange={(campoId, v) => setRespostasBriefing((old) => ({ ...old, [campoId]: v }))}
              />
              {!mostrarObs && (
                <button
                  type="button"
                  onClick={() => setMostrarObs(true)}
                  className="mt-2 text-[11px] text-blue-600 hover:underline"
                >
                  + adicionar observação (algo que não cabe nas perguntas)
                </button>
              )}
            </div>
          )}

          {/* Descrição livre: sempre visível quando NÃO há formulário; com
              formulário, só aparece se a pessoa clicar em "+ adicionar observação". */}
          <div className={formBriefing && !mostrarObs ? "hidden" : ""}>
            <label className="text-xs text-slate-600">
              {formBriefing ? "Observação" : "Descrição"}{" "}
              <span className="text-slate-400">(opcional, aceita @menção e formatação)</span>
            </label>
            {/* Barra de formatação ligada (melhoria 6): negrito/cor/listas sem
                código, igual aos comentários.
                data-editor-descricao: quando o modal é aberto EM CIMA do drawer
                (editar tarefa), o drawer mantém um listener global de paste que
                joga imagem colada no compositor de comentário — esse marcador faz
                ele PULAR (mesmo padrão do editor inline). Ver [[editor-rico-comentario]].
                onUploadImagem: na EDIÇÃO sobe direto (a tarefa já tem id). Na
                CRIAÇÃO guarda o arquivo e devolve uma URL temporária pra exibir
                na hora — o upload real acontece depois de criar a tarefa e a URL
                é trocada na descrição (resolverImagensPendentes). Antes o colar
                simplesmente não funcionava na criação. */}
            <div data-editor-descricao>
              <MentionTextarea
                value={descricao}
                onChange={setDescricao}
                usuarios={usuarios}
                toolbar
                rows={3}
                placeholder="Detalhes…"
                onUploadImagem={
                  editing?.id
                    ? (file) => uploadAnexo(editing.id, file).then((a) => anexoUrlAbsoluto(a.url))
                    : async (file) => guardarImagemPendente(file)
                }
              />
            </div>
          </div>

          {/* ALOCADOS EM UM CAMPO SÓ (pedido da Thainá, ago/2026: "tínhamos
              combinado de deixar todos juntos para não ter 3 campos diferentes").
              Antes eram 3: "Quem está fazendo" + "Aprovador" + "Quem mais está
              fazendo". Agora é UMA lista de pessoas — a 1ª é a principal (vira
              `atribuidoA`) e as demais são co-responsáveis (`responsaveis`),
              que é o que o backend espera. O aprovador virou um link discreto
              (a Rec não usa hoje, mas o recurso continua). */}
          <div>
            <label className="text-xs text-slate-600">
              Quem vai fazer{" "}
              <span className="text-slate-400">
                (a 1ª é a principal — as outras contam tempo também)
              </span>
            </label>
            <div className="flex flex-col gap-1.5 p-2 rounded-lg border border-slate-300 min-h-[42px] justify-center">
              {alocados.length === 0 && (
                <span className="text-xs text-slate-400 italic">
                  {ehEdicao ? "Ninguém alocado." : `Ninguém escolhido → fica com você (${user?.nome || "atual"}).`}
                </span>
              )}
              {alocados.map((id, i) => {
                const u = usuarios.find((x) => x.id === id);
                const principal = i === 0;
                const funcao = funcoesPessoa[id] || "";
                return (
                  <div key={id} className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border shrink-0 ${
                        principal
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-blue-50 border-blue-200 text-blue-800"
                      }`}
                    >
                      {principal && <span className="text-[9px] uppercase font-bold opacity-90">principal</span>}
                      <span>{u?.nome || id}</span>
                      {!principal && (
                        <button
                          type="button"
                          onClick={() => tornarPrincipal(id)}
                          className="hover:text-blue-600"
                          title="Tornar a pessoa principal desta tarefa"
                        >
                          ★
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removerAlocado(id)}
                        className={principal ? "hover:text-red-200" : "hover:text-red-600"}
                        title="Remover"
                      >
                        <X size={11} />
                      </button>
                    </span>
                    {/* FUNÇÃO na hora da inclusão (pedido da Thainá, ago/2026):
                        o que ESTA pessoa faz NESTE card. Catálogo em
                        Admin → Funções na tarefa. */}
                    <select
                      value={funcao}
                      onChange={(e) => setFuncoesPessoa((m) => ({ ...m, [id]: e.target.value }))}
                      className="text-xs px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-slate-600 dark:text-slate-300 outline-none min-w-0 flex-1"
                      title="O que esta pessoa faz nesta tarefa"
                    >
                      <option value="">— sem função</option>
                      {funcoesCatalogo.map((f) => (
                        <option key={f.id} value={f.nome}>{f.nome}</option>
                      ))}
                      {funcao && !funcoesCatalogo.some((f) => f.nome === funcao) && (
                        <option value={funcao}>{funcao} (fora do catálogo)</option>
                      )}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="mt-1.5">
              <SearchableSelect
                items={usuarios.filter((u) => !alocados.includes(u.id))}
                value=""
                onChange={(id) => { if (id) adicionarAlocado(id); }}
                getId={(u) => u.id}
                getLabel={(u) => u.nome}
                getSecondary={(u) => `${u.email} · ${u.perfil}`}
                placeholder="Adicionar pessoa…"
              />
            </div>

            {/* Aprovador: escondido até alguém precisar (a Rec não usa hoje). */}
            {!mostrarAprovador ? (
              <button
                type="button"
                onClick={() => setMostrarAprovador(true)}
                className="mt-1.5 text-[11px] text-blue-600 hover:underline"
              >
                + definir aprovador (opcional)
              </button>
            ) : (
              <div className="mt-2">
                <label className="text-xs text-slate-600">Aprovador <span className="text-slate-400">(opcional)</span></label>
                <SearchableSelect
                  items={usuarios}
                  value={aprovadorId}
                  onChange={setAprovadorId}
                  getId={(u) => u.id}
                  getLabel={(u) => u.nome}
                  getSecondary={(u) => `${u.email} · ${u.perfil}`}
                  placeholder="Sem aprovação"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Projeto <span className="text-red-500">*</span></label>
              <SearchableSelect
                items={[{ id: AVULSA, nome: "— Sem projeto (avulsa) —" }, ...projetos]}
                value={projetoId}
                onChange={setProjetoId}
                getId={(p) => p.id}
                // Nome do cliente junto (e buscável) — ver `rotuloProjeto`.
                getLabel={(p) => rotuloProjeto(p)}
                placeholder="Selecione o projeto…"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">
                Prazo <span className="text-red-500">*</span>{" "}
                <span className="text-slate-400">(hora opcional)</span>
              </label>
              <div className="flex gap-1.5">
                <input
                  type="date"
                  value={prazo}
                  onChange={(e) => setPrazo(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                />
                <input
                  type="time"
                  value={prazoHora}
                  onChange={(e) => setPrazoHora(e.target.value)}
                  className="w-[6.5rem] px-2 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                  title="Hora (opcional). Vazio = data inteira."
                />
                {prazoHora && (
                  <button
                    type="button"
                    onClick={() => setPrazoHora("")}
                    className="px-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 text-xs"
                    title="Remover hora"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* MELHORIA 16 — data combinada com o cliente (só gestão vê/seta). */}
          {can(["gestor", "admin"]) && (
            <div>
              <label className="text-xs text-slate-600 flex items-center gap-1">
                🔒 Entrega ao cliente <span className="text-slate-400">(data combinada — pauta; funcionário não vê)</span>
              </label>
              <input
                type="date"
                value={dataEntregaCliente}
                onChange={(e) => setDataEntregaCliente(e.target.value)}
                className="w-full sm:w-1/2 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                title="Data acordada com o cliente, separada do prazo interno. Alimenta a pauta em Calendário → Entregas. Só gestão vê."
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">
                Link da pasta do Drive{" "}
                {ehCaptacao
                  ? <span className="text-slate-400">(opcional em captação)</span>
                  : <span className="text-red-500">*</span>}
              </label>
              <input
                type="url"
                value={linkDrive}
                onChange={(e) => setLinkDrive(e.target.value)}
                placeholder="https://drive.google.com/…"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">
                Esforço <span className="text-red-500">*</span> <span className="text-slate-400">(horas)</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={esforco}
                onChange={(e) => setEsforco(e.target.value)}
                placeholder="01h00"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm font-mono"
                title='Aceita "1h30", "1:30", "1.5", "90m"'
              />
              {!ehEdicao && mediaTipo && mediaTipo.comTempo > 0 && mediaTipo.mediaExecutadaMs != null && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  ⏱️ Tarefas desse tipo levam em média <strong>{formatarEsforco(mediaTipo.mediaExecutadaMs / 3600000)}</strong>{" "}
                  ({mediaTipo.comTempo} {mediaTipo.comTempo === 1 ? "amostra" : "amostras"}).
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-600">
                Quantidade de peças <span className="text-slate-400">(entregues)</span>
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={quantidadePecas}
                onChange={(e) => setQuantidadePecas(e.target.value)}
                placeholder="1"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                title="Quantas peças este card entrega (ex.: 3 artes). Conta na produtividade por pessoa e por tipo."
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Recorrência</label>
              <select
                value={recorrencia}
                onChange={(e) => setRecorrencia(e.target.value as any)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
              >
                <option value="">Sem recorrência</option>
                <option value="diaria">Diária</option>
                <option value="semanal">Semanal</option>
                <option value="mensal">Mensal</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 border-t border-slate-100 pt-3">
            <input type="checkbox" checked={visivelParaCliente} onChange={(e) => setVisivelParaCliente(e.target.checked)} />
            <span>Visível ao cliente no portal (se o projeto tiver cliente vinculado)</span>
          </label>

          {!ehEdicao && (
          <div>
            <label className="text-xs text-slate-600">Checklist inicial <span className="text-slate-400">(opcional)</span></label>
            <div className="space-y-1 mt-1">
              {subitens.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 px-2 py-1 bg-slate-50 rounded">{s}</span>
                  <button
                    onClick={() => setSubitens(subitens.filter((_, idx) => idx !== i))}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={novoSub}
                  onChange={(e) => setNovoSub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && novoSub.trim()) {
                      e.preventDefault();
                      setSubitens([...subitens, novoSub.trim()]);
                      setNovoSub("");
                    }
                  }}
                  placeholder="Adicionar item e Enter…"
                  className="flex-1 px-3 py-1.5 rounded border border-slate-200 text-sm outline-none focus:border-blue-400"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (novoSub.trim()) {
                      setSubitens([...subitens, novoSub.trim()]);
                      setNovoSub("");
                    }
                  }}
                  className="bg-slate-100 hover:bg-slate-200 px-2 py-1.5 rounded"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
          )}

          {!ehEdicao && (
            <div>
              <label className="text-xs text-slate-600 flex items-center gap-1.5">
                <Paperclip size={12} /> Anexos <span className="text-slate-400">(opcional — imagens, PDFs, áudios, etc.)</span>
              </label>

              {/* Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragHover(true); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragHover(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragHover(false); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setDragHover(false);
                  adicionarArquivos(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                onPaste={(e) => {
                  const files: File[] = [];
                  for (const it of Array.from(e.clipboardData.items)) {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                  }
                  if (files.length > 0) adicionarArquivos(files);
                }}
                className={`mt-1 cursor-pointer rounded-lg border-2 border-dashed transition-colors text-center px-4 py-5 ${
                  dragHover
                    ? "border-blue-400 bg-blue-50"
                    : "border-slate-300 hover:border-blue-300 hover:bg-slate-50"
                }`}
              >
                <UploadCloud size={20} className={`mx-auto ${dragHover ? "text-blue-500" : "text-slate-400"}`} />
                <p className="text-xs text-slate-600 mt-1">
                  <span className="font-medium text-blue-600">Clique pra escolher</span> ou arraste arquivos aqui
                </p>
                <p className="text-[10px] text-slate-400">Cole imagens com Ctrl+V também</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    adicionarArquivos(e.target.files);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                />
              </div>

              {/* Lista de arquivos selecionados */}
              {anexos.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {anexos.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-slate-50 text-xs">
                      <IconePorTipo file={f} />
                      <span className="flex-1 truncate text-slate-700" title={f.name}>{f.name}</span>
                      <span className="text-[10px] text-slate-400 tabular-nums">{formatarBytes(f.size)}</span>
                      <button
                        type="button"
                        onClick={() => removerAnexo(i)}
                        className="text-slate-400 hover:text-red-500 p-0.5"
                        title="Remover"
                      >
                        <X size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {progressoUpload && (
                <p className="text-[11px] text-slate-500 mt-2">
                  Enviando anexos: {progressoUpload.enviados} / {progressoUpload.total}…
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {!ehEdicao && faltando.length > 0 && (
            <span className="text-[11px] text-amber-600 mr-auto">
              Falta preencher: {faltando.join(", ")}.
            </span>
          )}
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">
            Cancelar
          </button>
          <button
            onClick={() => (ehEdicao ? updateM.mutate() : criarM.mutate())}
            disabled={!podeSalvar || submitting}
            className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            title={progressoUpload ? `Enviando ${progressoUpload.enviados}/${progressoUpload.total} anexos…` : undefined}
          >
            {submitting ? "Salvando…" : ehEdicao ? "Salvar alterações" : "Criar tarefa"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IconePorTipo({ file }: { file: File }) {
  const m = file.type;
  if (m.startsWith("image/")) return <ImageIcon size={14} className="text-emerald-600 shrink-0" />;
  if (m.startsWith("audio/")) return <Music size={14} className="text-purple-600 shrink-0" />;
  if (m.startsWith("video/")) return <Film size={14} className="text-blue-600 shrink-0" />;
  return <FileText size={14} className="text-slate-500 shrink-0" />;
}

function formatarBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
